import { Icon, IconName, Icons } from '@/styles/Icon';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { LoadingSpinnerSmall } from '../common/LoadingSpinner';

export const StyledLogoButton = styled.button`
  /* 기본 크기 */
  width: fit-content;
  height: 33px;
  
  /* 배경 및 보더 초기화 */
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  
  /* 텍스트 스타일 */
  font-weight: 800;
  font-style: normal;
  font-size: 28px;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
  
  /* 그래디언트 텍스트 */
  background: linear-gradient(90deg, #7BD7FB 0%, #99A7FF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  /* 투명도 및 상호작용 */
  opacity: 1;
  cursor: pointer;
  
  /* 트랜지션 */
  transition: all 0.3s ease;
  
  /* 호버 효과 */
  &:hover {
    opacity: 0.8;
  }
  
  /* 포커스 효과 */
  &:focus {
    outline: none;
    filter: drop-shadow(0 0 8px rgba(123, 215, 251, 0.4));
  }
  
  /* 비활성화 상태 */
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  /* 접근성 향상 */
  &:focus-visible {
    outline: 2px solid rgba(123, 215, 251, 0.5);
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

// 버튼 Props 타입 정의
export interface LogoButtonProps {
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  className?: string;
}

// 메인 로고 버튼
export const LogoButton: React.FC<LogoButtonProps> = ({
  onClick,
  disabled = false,
  className,
  ...props
}) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    onClick?.(event);
  };

  return (
    <StyledLogoButton
      onClick={handleClick}
      disabled={disabled}
      className={className}
      {...props}
    >
      GRAB MUSIC
    </StyledLogoButton>
  );
};

/* 
사용 방법:

import { LogoButton } from '@/components/common/LogoButton/LogoButton';
import { useNavigate } from 'react-router-dom';

// 기본 사용
const navigate = useNavigate();
<LogoButton onClick={() => navigate('/')} />

// 비활성화 상태
<LogoButton disabled />

// 클래스명 추가
<LogoButton 
  className="header-logo"
  onClick={handleHomeClick}
/>
*/

// 기본 라운드 버튼
interface RoundButtonProps {
  onClick?: () => void;
    disabled?: boolean;
    children: React.ReactNode;
    className?: string;
    selected?: boolean;
  }
export const RoundButton: React.FC<RoundButtonProps> = ({
    onClick,
    disabled = false,
    children,
    className,
    selected= false
  }) => {
    return (
      <StyledRoundButton
        onClick={onClick}
        disabled={disabled}
        className={className}
        selected={selected}
      >
        {children}
      </StyledRoundButton>
    );
}
const StyledRoundButton = styled.button<{ selected: boolean }>`
    display: flex;
    justify-content: center;
    align-items: center;
    width: fit-content;
    height: 40px;
    border-radius: 999px;
    padding: 4px 16px;
    gap: 10px;
    
    /* selected 상태에 따른 스타일 분기 */
    background: ${({ selected, theme }) => 
      selected ? theme.colors.primary : 'transparent'};
    border: ${({ selected }) => 
      selected ? 'none' : '1px solid #E0E0E0'};
    color: ${({ selected, theme }) => 
      selected ? theme.colors.fontWhite : theme.colors.fontBlack};
    
    font-size: 16px;
    font-weight: 700;
    font-family: ${({ theme }) => theme.fonts.pretendard};
    cursor: pointer;

    /* 아이콘 스타일 */
    svg {
      width: 20px;
      height: 20px;
      fill: ${({ selected, theme }) => selected ? theme.colors.fontWhite : theme.colors.fontBlack};
    }

    &:hover:not(:disabled) {
      background: ${({ selected, theme }) => 
        selected ? `${theme.colors.primary}dd` : '#F5F5F5'};
      border-color: ${({ selected }) => 
        selected ? 'transparent' : '#D0D0D0'};
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      background: ${({ theme }) => theme.colors.btnDisabled};
      cursor: not-allowed;
      opacity: 0.6;
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba(118, 160, 255, 0.3);
    }
`;



// ===== 통합 GradientButton 컴포넌트 =====
export interface GradientButtonProps {
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  loading?: boolean;
  width?: number | string;
  height?: number | string;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  variant?: 'gradient' | 'primary' | 'secondary' | 'gray' | 'signout' | 'red';
  icon?: IconName | React.ReactNode;
  iconSize?: number | string;
  iconPosition?: 'left' | 'right';
  fontSize?: number | string;
  size?: 'small' | 'medium';
  style?: React.CSSProperties;
}

export const StyledGradientButton = styled.button<{
  width?: number | string;
  height?: number | string;
  disabled?: boolean;
  $variant?: 'gradient' | 'primary' | 'secondary' | 'gray' | 'signout' | 'red';
  $hasIcon?: boolean;
  $fontSize?: number | string;
  $size?: 'small' | 'medium';
  style?: React.CSSProperties;
}>`
  /* 기본 크기 및 레이아웃 */
  width: ${({ width }) => typeof width === 'number' ? `${width}px` : width || 'auto'};
  height: ${({ height }) => typeof height === 'number' ? `${height}px` : height || 'auto'};
  min-width: ${({ $hasIcon, $variant }) => {
    if ($variant === 'signout') return '137px';
    return $hasIcon ? 'auto' : '135px';
  }};
  
  /* size에 따른 패딩 */
  padding: ${({ $size, $hasIcon, $variant }) => {
    if ($variant === 'signout') return '8px 20px';
    if ($hasIcon) return '12px 20px';
    return $size === 'small' ? '12px 20px' : '16px 24px';
  }};
  
  /* 보더 및 모양 */
  border: none;
  border-radius: 10px;
  
  /* 배경 스타일 */
  background: ${({ disabled, theme, $variant }) => {
    if (disabled) return theme.colors.btnDisabled;
    if ($variant === 'signout') return '#C1C1C1';
    if ($variant === 'gray') return theme.colors.bgGray[600];
    if ($variant === 'primary') return theme.colors.primary;
    if ($variant === 'secondary') return theme.colors.bgWhite;
    if ($variant === 'red') return theme.colors.reportRed;
    return 'linear-gradient(90deg, #6B8EFF 0%, #9FE2FF 100%)';
  }};
  
  /* 텍스트 스타일 */
  color: ${({ theme, $variant }) => {
    if ($variant === 'signout') return theme.colors.fontWhite;
    return $variant === 'secondary' ? theme.colors.fontBlack : theme.colors.fontWhite
  }};
  font-family: ${({ theme }) => theme.fonts.pretendard};
  /* fontSize prop이 있으면 우선 적용, 없으면 size에 따른 기본값 */
  font-size: ${({ $fontSize, $size, $variant }) => {
    if ($fontSize) {
      return typeof $fontSize === 'number' ? `${$fontSize}px` : $fontSize;
    }
    if ($variant === 'signout') return '24px';
    return $size === 'small' ? '22px' : '24px';
  }};
  font-weight: ${({ $hasIcon, $variant }) => {
    if ($variant === 'signout') return 700;
    return $hasIcon ? 500 : 600;
  }};
  line-height: ${({ $hasIcon, $variant }) => {
    if ($variant === 'signout') return '29px';
    return $hasIcon ? 'normal' : '1';
  }};
  text-align: center;
  white-space: nowrap;
  
  /* 플렉스 레이아웃 */
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  
  /* 투명도 및 상호작용 */
  opacity: ${({ disabled }) => disabled ? 0.6 : 1};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  
  /* 호버 효과 */
  &:hover:not(:disabled) {
    ${({ $variant, theme }) => {
      if ($variant === 'signout') return `background: #A8A8A8;`;
      if ($variant === 'primary') return `background: ${theme.colors.primary}dd;`;
      if ($variant === 'red') return `background: ${theme.colors.reportRed}dd;`;
      return `background: linear-gradient(90deg, #5A7EFF 0%, #8FD7FF 100%);`;
    }}
  }
  
  /* 액티브 효과 */
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(107, 142, 255, 0.2);
  }
  
  /* 포커스 효과 */
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(107, 142, 255, 0.3);
  }
`;

// 통합 GradientButton 컴포넌트
export const GradientButton: React.FC<GradientButtonProps> = ({
  children,
  onClick,
  disabled = false,
  loading = false,
  width,
  height,
  type = 'button',
  className,
  variant = 'gradient',
  icon,
  iconSize = 20,
  iconPosition = 'left',
  fontSize,
  size = 'medium',
  style,
  ...props
}) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) return;
    onClick?.(event);
  };

  const renderIcon = () => {
    if (!icon) return null;
    
    if (typeof icon === 'string' && icon in Icons) {
      return <Icon name={icon as IconName} size={iconSize} />;
    }
    
    return icon;
  };

  const hasIcon = !!icon;

  return (
    <StyledGradientButton
      type={type}
      onClick={handleClick}
      disabled={disabled || loading}
      width={width}
      height={height}
      className={className}
      $variant={variant}
      $hasIcon={hasIcon}
      $fontSize={fontSize}
      $size={size}
      style={style}
      {...props}
    >
      {loading ? (
        <LoadingSpinnerSmall />
      ) : (
        <>
          {icon && iconPosition === 'left' && (
            <IconWrapper $size={iconSize}>{renderIcon()}</IconWrapper>
          )}
          {children}
          {icon && iconPosition === 'right' && (
            <IconWrapper $size={iconSize}>{renderIcon()}</IconWrapper>
          )}
        </>
      )}
    </StyledGradientButton>
  );
};

// 아이콘 래퍼
const IconWrapper = styled.span<{ $size?: number | string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => typeof $size === 'number' ? `${$size}px` : $size || '20px'};
  height: ${({ $size }) => typeof $size === 'number' ? `${$size}px` : $size || '20px'};
  flex-shrink: 0;
  
  img, svg {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const IconTextButton: React.FC<{
  icon?: IconName | React.ReactNode;
  text: string;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  iconSize?: number | string;
}> = ({ icon, text, ...props }) => (
  <GradientButton variant="primary" icon={icon} {...props}>
    {text}
  </GradientButton>
);

// Buy / Purchased / Sold out 버튼 컴포넌트
interface BuyButtonProps {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  isPurchased?: boolean;
  soldOut?: boolean;
  salesEnded?: boolean;
  isScheduled?: boolean;
  isInCart?: boolean;
  className?: string;
  size?: 'small' | 'medium';
  showIcon?: boolean;
  loading?: boolean;
}

export const BuyButtonComponent: React.FC<BuyButtonProps> = ({ 
  onClick, 
  isPurchased = false, 
  soldOut = false, 
  salesEnded = false,
  isScheduled = false,
  isInCart = false,
  className,
  size = 'small',
  showIcon = true,
  loading = false,
}) => {
  const getButtonText = () => {
    if (isScheduled) return 'Scheduled';
    if (isPurchased) return 'Purchased';
    if (soldOut) return 'Sold Out';
    if (salesEnded) return 'Sales Ended';
    if (isInCart) return 'In Cart';
    return 'Buy';
  };
  
  const navigate = useNavigate();
  const isDisabled = isScheduled || isPurchased || soldOut || salesEnded;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    // ✅ 로딩 중에는 클릭 무시
    if (loading) return;

    // ✅ In Cart 상태일 때는 장바구니로 이동
    if (isInCart) {
      navigate('/cart');
      return;
    }
    
    if (!isDisabled && onClick) {
      onClick(e);
    }
  };

  // 스타일 결정 로직 추가
  const getStyle = () => {
    if (isInCart) {
      return {
        background: '#414141',
        cursor: 'pointer',
        color: '#ffffff',
      };
    }
    return undefined;
  };

  // 아이콘 결정 로직 수정
  const getIcon = () => {
    if (!showIcon) return undefined;
    if (isPurchased || soldOut || salesEnded || isScheduled) return undefined;
    return isInCart ? "check" : "cartWhite"; // ✅ 체크 아이콘으로 시각적 피드백
  };

  return (
    <GradientButton
      onClick={handleClick}
      disabled={isDisabled}
      loading={loading}
      className={className}
      size={size}
      icon={getIcon()}
      iconSize={20}
      style={getStyle()}
    >
      {getButtonText()}
    </GradientButton>
  );
};

// Cart 버튼 컴포넌트
export const CartButton: React.FC<{
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  isScheduled?: boolean;
  isInCart?: boolean;
  soldOut?: boolean;
  salesEnded?: boolean;
  isPurchased?: boolean;
  loading?: boolean;
}> = ({ onClick, isScheduled = false, isInCart = false, soldOut = false, salesEnded = false, isPurchased = false, loading = false }) => {
  // 버튼 텍스트 결정
  const getButtonText = () => {
    if (isScheduled) return 'Scheduled';
    if (soldOut) return 'Sold Out';
    if (salesEnded) return 'Sales Ended';
    if (isPurchased) return 'Purchased';
    if (isInCart) return 'Remove from Cart';
    return 'Add to Cart';
  };

  // 버튼 비활성화 여부
  const isDisabled = isScheduled || soldOut || salesEnded || isPurchased;

  // 버튼 스타일 결정
  const getButtonStyle = () => {
    if (soldOut || salesEnded) {
      return {
        background: '#C4C4C4',
        cursor: 'not-allowed'
      };
    }
    if (isInCart) {
      return {
        border: '1px solid #CECECE',
        background: '#F3F3F3',
        fontWeight: 700,
        fontSize: '24px',
      };
    }
    return undefined;
  };

  // 아이콘 결정
  const getIcon = () => {
    if (soldOut || salesEnded || isScheduled) return undefined;
    return isInCart ? "minus" : "cartWhite";
  };

  return (
    <GradientButton
      onClick={onClick}
      loading={loading}
      variant={isDisabled ? "gray" : (isInCart ? "gray" : "gradient")}
      icon={getIcon()}
      iconSize={24}
      size="small"
      width="100%"
      height="61px"
      disabled={isDisabled}
      style={getButtonStyle()}
    >
      {getButtonText()}
    </GradientButton>
  );
};

// ChipBtn 컴포넌트 - Preview, Purchased, Verified 상태 표시
interface ChipBtnProps {
  status?: 'Preview' | 'Purchased' | 'Verified';
  onClick?: () => void;
}

export const ChipBtn: React.FC<ChipBtnProps> = ({ 
  status = 'Preview', 
  onClick,
}) => {
  return (
    <StyledChipBtn 
      $status={status} 
      onClick={onClick}
      type="button"
    >
      {status}
    </StyledChipBtn>
  );
};

const StyledChipBtn = styled.button<{ $status: 'Preview' | 'Purchased' | 'Verified' }>`
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 400;
  transition: all 0.2s ease;
  cursor: default;
  
  /* status에 따른 스타일 분기 */
  background: ${({ $status, theme }) => ($status === 'Purchased' || $status === 'Verified') ? theme.colors.fontWhite : 'transparent'};
  
  color: ${({ $status, theme }) => ($status === 'Purchased' || $status === 'Verified') ? '#9D9D9D' : theme.colors.fontWhite};
  
  border: 1px solid ${({ $status, theme }) => ($status === 'Purchased' || $status === 'Verified') ? theme.colors.fontWhite : theme.colors.borderGray};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

// Verify chip 버튼 컴포넌트
// 검증 전, 검증 완료, 검증 실패
export const VerifiedChipBtn: React.FC<{ status?: 'Unverified' | 'Verified' | 'Invalid', onClick?: React.MouseEventHandler<HTMLButtonElement>; }> = ({ status='Unverified', onClick }) => {
  return (
    <StyledVerifiedChipBtn
      $status={status}
      onClick={onClick}
      type="button"
    >
      {status}
    </StyledVerifiedChipBtn>
  );
};

const StyledVerifiedChipBtn = styled.button<{ $status: 'Unverified' | 'Verified' | 'Invalid' }>`
  padding: 6px 16px;
  border-radius: 999px;
  font-size: 16px;
  font-weight: 700;
  transition: all 0.2s ease;
  cursor: default;
  min-width: fit-content;
  width: fit-content;

  /* status에 따른 스타일 분기 */
  background: ${({ $status, theme }) => ($status === 'Verified') ? theme.colors.verifiedGreen : ($status === 'Invalid') ? theme.colors.errorRed : theme.colors.borderGray};
  color: ${({ theme }) => theme.colors.fontWhite};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

